import AddIcon from '@atlaskit/icon/core/migration/add';
import Tooltip from '@atlaskit/tooltip';
import { useAnalytics, getDatabaseContainerType } from '@databases/analytics';
import { useFlaggingCallback } from '@databases/banners';
import { messages } from '@databases/intl/messages';
import { HoverProps } from '@databases/react';
import { defaultStoreUtils } from '@databases/redux';
import { deselectAll } from '@databases/redux/cellSelections/actions';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import { useScope } from '@databases/scope';
import { DatabaseChangeType } from '@databases/types/redux';
import { createEntry } from '@databases/utils/database/utils';
import useView from '@databases/views/useView';
import React, { FunctionComponent } from 'react';
import { useIntl } from 'react-intl-next';
import styled, { css } from 'styled-components';

import { Position } from '../contextual-menu/type';

import { StyledDot } from './AddFieldDot';

const InsertEntryDot = styled(StyledDot)`
  left: calc(50% - 5px);
  ${({ position }) =>
    position === Position.AFTER
      ? css`
          bottom: -5.75px;
        `
      : css`
          top: -5px;
        `}
`;

export interface AddEntryDotProps {
  contentId: string;
  entryId: string;
  fieldId: string;
  canEditContent: boolean;
  position: Position;
  hoverProps?: HoverProps;
  editingIds: React.MutableRefObject<
    Set<{ entryId: string; fieldId: string | undefined }>
  >;
}

/**
 * Renders a dot that for adding a new entry before or after the current entry.
 */
const AddEntryDot: FunctionComponent<AddEntryDotProps> = ({
  contentId,
  entryId,
  fieldId,
  canEditContent,
  position,
  hoverProps,
  editingIds,
}) => {
  const store = defaultStoreUtils.useStore();
  const dispatch = defaultStoreUtils.useDispatch();
  const client = useAnalytics();
  const scope = useScope();
  const {
    view: { settings: viewSettings },
  } = useView();

  const { formatMessage } = useIntl();

  const handleInsertRow = useFlaggingCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();
      const databaseDraft = selectDatabaseDraftByContentId(store.getState(), {
        contentId,
        scope,
        includeDeleted: true,
      });
      const entry = createEntry(databaseDraft, viewSettings);
      const currentEntryIndex = databaseDraft.entries.indexOf(entryId);
      const previousOrNextEntryIndex =
        position === Position.AFTER ? currentEntryIndex + 1 : currentEntryIndex;
      dispatch(
        addChangeToDraft(contentId, scope, {
          type: DatabaseChangeType.ADD_ENTRY,
          entry,
          index: previousOrNextEntryIndex,
        })
      );
      dispatch(deselectAll());
      editingIds?.current.add({ entryId: entry.id, fieldId: fieldId });
      await client.sendInsertRowEvent({
        contentId: contentId,
        databaseContainer: getDatabaseContainerType(),
      });
    },
    [
      client,
      contentId,
      dispatch,
      editingIds,
      entryId,
      fieldId,
      position,
      scope,
      store,
      viewSettings,
    ]
  );

  if (!canEditContent) {
    return null;
  }
  return (
    <Tooltip
      content={() => formatMessage(messages['table-layout.insert-row-tooltip'])}
      position={position === Position.BEFORE ? 'top' : 'bottom'}
    >
      <InsertEntryDot onClick={handleInsertRow} position={position} {...hoverProps}>
        <AddIcon label="" LEGACY_size="small" />
      </InsertEntryDot>
    </Tooltip>
  );
};

export default AddEntryDot;
